(function () {
  'use strict';

  var SELECTORS = {
    drawer: '[data-cart-drawer]',
    overlay: '[data-cart-drawer-overlay]',
    panel: '[data-cart-drawer-panel]',
    close: '[data-cart-drawer-close]',
    toggle: '[data-cart-drawer-toggle]',
    items: '[data-cart-drawer-items]',
    count: '[data-cart-drawer-count]',
    subtotal: '[data-cart-drawer-subtotal]',
    footer: '[data-cart-drawer-footer]',
    empty: '[data-cart-drawer-empty]',
    body: '[data-cart-drawer-body]',
    item: '[data-cart-drawer-item]',
    qtyInput: '[data-cart-drawer-qty-input]',
    qtyPlus: '[data-cart-drawer-qty-plus]',
    qtyMinus: '[data-cart-drawer-qty-minus]',
    remove: '[data-cart-drawer-remove]',
    headerCartCount: '[data-header-cart-count]'
  };

  var CartDrawer = {
    drawer: null,
    isOpen: false,
    isUpdating: false,

    init: function () {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) return;

      // JS hijacks the header cart link into a dialog trigger — say so to AT.
      // (Set here, not in Liquid, so the no-JS fallback keeps clean link semantics.)
      document.querySelectorAll(SELECTORS.toggle).forEach(function (t) {
        t.setAttribute('aria-haspopup', 'dialog');
      });

      this.bindEvents();
    },

    open: function () {
      if (this.isOpen) return;
      // Bidirectional dialog guard: if the newsletter popup is up, unwind it cleanly first
      // (its inert/Escape state would otherwise tangle with ours).
      if (window.NewsletterPopup && typeof window.NewsletterPopup.isOpen === 'function' && window.NewsletterPopup.isOpen()) {
        try { window.NewsletterPopup.close(); } catch (e) {}
      }
      this.isOpen = true;

      // Remember what to return focus to when the drawer closes (e.g. the cart icon),
      // unless an action already captured it before disabling its trigger (see addToCart).
      if (!this.previouslyFocused) {
        this.previouslyFocused = document.activeElement;
      }

      this.drawer.removeAttribute('inert');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.setAttribute('aria-modal', 'true');
      this.setBackgroundInert(true); // hide page content from AT while modal — complements the Tab trap
      // 3P widgets lazily inject new body children — keep marking them while open.
      if (window.MutationObserver && !this.bgObserver) {
        var self = this;
        this.bgObserver = new MutationObserver(function () { if (self.isOpen) self.setBackgroundInert(true); });
      }
      if (this.bgObserver) this.bgObserver.observe(document.body, { childList: true });
      document.body.classList.add('cart-drawer-open');

      // Close existing ATC banner
      document.dispatchEvent(new Event('closeFlyouts'));

      // Move screen-reader / keyboard focus into the dialog so it is announced
      // ("Shopping cart, dialog") rather than leaving the user stranded on the page.
      var drawer = this.drawer;
      // preventScroll: the drawer is a fixed overlay, but it sits near the bottom of the DOM, so
      // a plain focus() makes the browser scroll the page down to it (page jumps to the bottom on
      // add-to-cart). preventScroll keeps focus behaviour without moving the page.
      requestAnimationFrame(function () { drawer.focus({ preventScroll: true }); });
    },

    close: function () {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.removeAttribute('aria-modal');
      this.drawer.setAttribute('inert', '');
      if (this.bgObserver) this.bgObserver.disconnect();
      this.setBackgroundInert(false);
      document.body.classList.remove('cart-drawer-open');

      // Return focus to the control that opened the drawer.
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
      this.previouslyFocused = null;
    },

    // Make everything outside the drawer inert/hidden to AT while it is open —
    // aria-modal alone is not honored by all AT, and the Tab trap doesn't stop the virtual cursor.
    setBackgroundInert: function (on) {
      var kids = document.body.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el === this.drawer || el.contains(this.drawer)) continue;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(el.tagName)) continue;
        // Never inert live regions — the drawer's own status region is a body-level
        // sibling, and inerting it silenced EVERY cart announcement while open.
        if (el.hasAttribute('aria-live') || el.getAttribute('role') === 'status' || el.hasAttribute('data-cart-drawer-status')) continue;
        if (on) {
          if (!el.hasAttribute('inert')) {
            // Remember the EXACT prior aria-hidden value (true/false/absent) so restore
            // reinstates it — presence-only tracking turned aria-hidden="false" into "true".
            el.setAttribute('data-cd-inerted', el.hasAttribute('aria-hidden') ? 'had:' + el.getAttribute('aria-hidden') : '1');
            el.setAttribute('inert', '');
            el.setAttribute('aria-hidden', 'true');
          }
        } else if (el.hasAttribute('data-cd-inerted')) {
          var marker = el.getAttribute('data-cd-inerted');
          el.removeAttribute('data-cd-inerted');
          el.removeAttribute('inert');
          if (marker.indexOf('had:') === 0) el.setAttribute('aria-hidden', marker.slice(4));
          else el.removeAttribute('aria-hidden');
        }
      }
    },

    // Keep Tab focus within the open drawer panel (wraps at both ends).
    trapFocus: function (e) {
      var panel = this.drawer.querySelector(SELECTORS.panel) || this.drawer;
      var nodes = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      var list = [];
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].offsetParent !== null || nodes[i].getClientRects().length) {
          list.push(nodes[i]);
        }
      }
      if (list.length === 0) {
        e.preventDefault();
        this.drawer.focus({ preventScroll: true });
        return;
      }
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === this.drawer || !panel.contains(active)) {
          e.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    },

    // Build the "added to cart" message from an /add.js line item.
    getAddedMessage: function (item) {
      var title = (item && (item.product_title || item.title)) || 'Item';
      return title + ' added to your cart';
    },

    // Announce a message to screen readers via the polite live region.
    announce: function (message) {
      var region = document.querySelector('[data-cart-drawer-status]');
      if (!region) return;
      region.textContent = '';
      window.setTimeout(function () { region.textContent = message; }, 50);
    },

    // Read a line item's product title from its rendered DOM (for focus-restore announcements).
    itemTitle: function (item) {
      var el = item && item.querySelector('.cart-drawer__item-title');
      return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
    },

    addToCart: function (form) {
      var self = this;
      var atcButton = form.querySelector('[data-product-atc]');

      // Capture the focus-return target NOW, before disabling the button strips its focus.
      self.previouslyFocused = atcButton || document.activeElement;

      // Disable button and show spinner
      if (atcButton) {
        atcButton.classList.add('processing');
        atcButton.setAttribute('disabled', 'disabled');
      }

      var formData = new FormData(form);

      fetch(this.getRoute('cart') + '/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        body: formData
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (data) {
              throw new Error(data.description || res.statusText);
            });
          }
          return res.json();
        })
        .then(function (addedItem) {
          // Re-enable button
          if (atcButton) {
            atcButton.classList.remove('processing');
            atcButton.removeAttribute('disabled');
          }

          // Fetch cart, render drawer, open it, and announce the addition to
          // screen readers (this path was previously silent).
          self.fetchAndRender().then(function () {
            self.open();
            self.announce(self.getAddedMessage(addedItem));
          });
        })
        .catch(function (err) {
          console.error('Add to cart error:', err);
          // Drawer won't open; clear the captured focus target so it doesn't go stale.
          self.previouslyFocused = null;
          // Re-enable button — and keep keyboard focus on it so the user isn't stranded.
          if (atcButton) {
            atcButton.classList.remove('processing');
            atcButton.removeAttribute('disabled');
            try { atcButton.focus({ preventScroll: true }); } catch (e) {}
          }

          // Show the error in a real role=alert near the ATC button. (The previous
          // message-banner clone used class 'active', which has NO CSS — display:none forever.)
          // No announce() here: role=alert already announces assertively — both would speak twice.
          var msg = err.message || 'Could not add item to cart. Please try again.';
          var host = atcButton ? (atcButton.closest('form') || atcButton.parentElement) : null;
          if (!host) self.announce(msg); // fallback only when there is nowhere to render the alert
          if (host) {
            var alert = host.querySelector('[data-cart-drawer-atc-error]');
            if (!alert) {
              alert = document.createElement('div');
              alert.setAttribute('data-cart-drawer-atc-error', '');
              alert.setAttribute('role', 'alert');
              alert.style.cssText = 'display:block;margin-top:8px;padding:8px 12px;color:#b3261e;border:1px solid #b3261e;border-radius:4px;background:#fff;font-size:0.9em;';
              host.appendChild(alert);
            }
            alert.textContent = msg;
          }
        });
    },

    fetchAndRender: function () {
      var self = this;
      return fetch(this.getRoute('cart') + '.js', {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          self.renderItems(cart);
          self.updateCount(cart.item_count);
          self.updateSubtotal(cart.total_price);
          self.updateHeaderCount(cart.item_count);
          self.toggleEmptyState(cart.item_count === 0);
        })
        .catch(function (err) {
          console.error('Cart drawer fetch error:', err);
        });
    },

    renderItems: function (cart) {
      var container = this.drawer.querySelector(SELECTORS.items);
      if (!container) return;

      if (cart.items.length === 0) {
        container.innerHTML = '';
        return;
      }

      var html = '';
      for (var i = 0; i < cart.items.length; i++) {
        html += this.renderSingleItem(cart.items[i]);
      }
      container.innerHTML = html;
    },

    renderSingleItem: function (item) {
      var imageHtml = '';
      if (item.image) {
        var sizedUrl = this.getSizedImageUrl(item.image, '160x');
        imageHtml = '<img src="' + sizedUrl + '" alt="' + this.escapeHtml(item.title) + '" loading="lazy" width="80" height="80">';
      }

      var variantHtml = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantHtml = '<span class="cart-drawer__item-variant">' + this.escapeHtml(item.variant_title) + '</span>';
      }

      return '<li class="cart-drawer__item" data-cart-drawer-item data-cart-drawer-item-key="' + item.key + '">' +
        '<a href="' + item.url + '" class="cart-drawer__item-image">' + imageHtml + '</a>' +
        '<div class="cart-drawer__item-details">' +
          '<a href="' + item.url + '" class="cart-drawer__item-title">' + this.escapeHtml(item.product_title) + '</a>' +
          variantHtml +
          '<div class="cart-drawer__item-quantity">' +
            '<button type="button" class="cart-drawer__qty-btn" data-cart-drawer-qty-minus aria-label="Decrease quantity, ' + this.escapeHtml(item.product_title) + '">-</button>' +
            '<input type="number" inputmode="numeric" class="cart-drawer__qty-input" value="' + item.quantity + '" min="1" data-cart-drawer-qty-input aria-label="Quantity, ' + this.escapeHtml(item.product_title) + '">' +
            '<button type="button" class="cart-drawer__qty-btn" data-cart-drawer-qty-plus aria-label="Increase quantity, ' + this.escapeHtml(item.product_title) + '">+</button>' +
          '</div>' +
          '<span class="cart-drawer__item-price">' + this.formatMoney(item.final_line_price) + '</span>' +
        '</div>' +
        '<button type="button" class="cart-drawer__item-remove" data-cart-drawer-remove aria-label="Remove ' + this.escapeHtml(item.product_title) + '">&times;</button>' +
      '</li>';
    },

    updateQuantity: function (key, quantity, context) {
      // Don't silently drop a concurrent update — tell the user to wait (a11y: 4.1.3).
      if (this.isUpdating) { this.announce('Updating cart, please wait'); return; }
      this.isUpdating = true;
      context = context || {};

      var self = this;
      fetch(this.getRoute('cart') + '/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          self.renderItems(cart);
          self.updateCount(cart.item_count);
          self.updateSubtotal(cart.total_price);
          self.updateHeaderCount(cart.item_count);
          self.toggleEmptyState(cart.item_count === 0);
          // renderItems() replaced innerHTML, destroying the button the user was on —
          // restore focus to the equivalent control and announce the change (4.1.3 / 2.4.3).
          self.restoreFocusAndAnnounce(cart, key, quantity, context);
          self.isUpdating = false;
        })
        .catch(function (err) {
          console.error('Cart drawer update error:', err);
          self.announce('Sorry, we could not update your cart. Please try again.');
          self.isUpdating = false;
        });
    },

    // After a re-render, put keyboard focus back where it was and tell screen readers what changed.
    restoreFocusAndAnnounce: function (cart, key, requestedQty, context) {
      var container = this.drawer.querySelector(SELECTORS.items);
      var line = null;
      for (var i = 0; i < cart.items.length; i++) { if (cart.items[i].key === key) { line = cart.items[i]; break; } }
      var safeKey = (window.CSS && CSS.escape) ? CSS.escape(key) : key.replace(/"/g, '\\"');
      var item = container ? container.querySelector('[data-cart-drawer-item-key="' + safeKey + '"]') : null;

      if (cart.item_count === 0) {
        this.announce('Your cart is now empty');
        var closeBtn = this.drawer.querySelector(SELECTORS.close);
        if (closeBtn) { closeBtn.focus({ preventScroll: true }); } else { this.drawer.focus({ preventScroll: true }); }
        return;
      }
      if (!item) {
        // The line was removed but other items remain.
        this.announce((context.title || 'Item') + ' removed from your cart');
        var firstRemove = container.querySelector(SELECTORS.remove);
        if (firstRemove) { firstRemove.focus({ preventScroll: true }); } else { this.drawer.focus({ preventScroll: true }); }
        return;
      }
      // Item still present — focus the same control the user activated, announce the new quantity.
      var target = null;
      if (context.action === 'plus') target = item.querySelector(SELECTORS.qtyPlus);
      else if (context.action === 'minus') target = item.querySelector(SELECTORS.qtyMinus);
      else if (context.action === 'input') target = item.querySelector(SELECTORS.qtyInput);
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
      var newQty = line ? line.quantity : requestedQty;
      var title = (line && line.product_title) || context.title || 'Item';
      this.announce(title + ', quantity ' + newQty);
    },

    updateCount: function (count) {
      var els = this.drawer.querySelectorAll(SELECTORS.count);
      for (var i = 0; i < els.length; i++) {
        els[i].textContent = count;
      }
    },

    updateSubtotal: function (totalPrice) {
      var el = this.drawer.querySelector(SELECTORS.subtotal);
      if (el) {
        el.innerHTML = this.formatMoney(totalPrice);
      }
    },

    updateHeaderCount: function (count) {
      var badges = document.querySelectorAll(SELECTORS.headerCartCount);
      for (var i = 0; i < badges.length; i++) {
        badges[i].setAttribute('data-header-cart-count', count > 0 ? count : '');
        if (count > 0) {
          badges[i].classList.add('visible');
        } else {
          badges[i].classList.remove('visible');
        }
      }
    },

    toggleEmptyState: function (isEmpty) {
      var emptyEl = this.drawer.querySelector(SELECTORS.empty);
      var footerEl = this.drawer.querySelector(SELECTORS.footer);
      if (emptyEl) emptyEl.style.display = isEmpty ? '' : 'none';
      if (footerEl) footerEl.style.display = isEmpty ? 'none' : '';
    },

    bindEvents: function () {
      var self = this;

      // Intercept product form submissions in capture phase (fires before Empire's handler)
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form.hasAttribute || !form.hasAttribute('data-product-form')) return;
        if (document.body.classList.contains('template-cart')) return;

        e.preventDefault();
        e.stopPropagation();

        self.addToCart(form);
      }, true);

      // Close buttons (X and Continue Shopping)
      this.drawer.addEventListener('click', function (e) {
        if (e.target.closest(SELECTORS.close)) {
          self.close();
          return;
        }

        // Overlay click
        if (e.target.closest(SELECTORS.overlay)) {
          self.close();
          return;
        }

        // Quantity minus — clamp at 1; removal must be the explicit Remove action only.
        var minusBtn = e.target.closest(SELECTORS.qtyMinus);
        if (minusBtn) {
          var item = minusBtn.closest(SELECTORS.item);
          var key = item.getAttribute('data-cart-drawer-item-key');
          var input = item.querySelector(SELECTORS.qtyInput);
          var qty = parseInt(input.value, 10);
          if (qty <= 1) { self.announce('Quantity is already 1. Use Remove to delete this item.'); return; }
          self.updateQuantity(key, qty - 1, { action: 'minus', title: self.itemTitle(item) });
          return;
        }

        // Quantity plus
        var plusBtn = e.target.closest(SELECTORS.qtyPlus);
        if (plusBtn) {
          var item = plusBtn.closest(SELECTORS.item);
          var key = item.getAttribute('data-cart-drawer-item-key');
          var input = item.querySelector(SELECTORS.qtyInput);
          var qty = parseInt(input.value, 10);
          self.updateQuantity(key, qty + 1, { action: 'plus', title: self.itemTitle(item) });
          return;
        }

        // Remove
        var removeBtn = e.target.closest(SELECTORS.remove);
        if (removeBtn) {
          var item = removeBtn.closest(SELECTORS.item);
          var key = item.getAttribute('data-cart-drawer-item-key');
          self.updateQuantity(key, 0, { action: 'remove', title: self.itemTitle(item) });
          return;
        }
      });

      // Quantity input change. Blank/invalid input REVERTS (announced) rather than
      // mapping to 0 — typing should never silently delete a line item.
      this.drawer.addEventListener('change', function (e) {
        if (e.target.matches(SELECTORS.qtyInput)) {
          var item = e.target.closest(SELECTORS.item);
          var key = item.getAttribute('data-cart-drawer-item-key');
          var qty = parseInt(e.target.value, 10);
          if (isNaN(qty) || qty < 1) {
            var prev = parseInt(e.target.getAttribute('value'), 10);
            e.target.value = isNaN(prev) || prev < 1 ? 1 : prev;
            self.announce('Quantity unchanged. Use Remove to delete this item.');
            return;
          }
          if (self.isUpdating) {
            // Don't leave an uncommitted number on screen — revert and say so (4.1.3).
            var committed = parseInt(e.target.getAttribute('value'), 10);
            e.target.value = isNaN(committed) || committed < 1 ? 1 : committed;
            self.announce('Cart is updating — quantity unchanged, please try again.');
            return;
          }
          self.updateQuantity(key, qty, { action: 'input', title: self.itemTitle(item) });
        }
      });

      // Keyboard handling while the drawer is open: Escape closes, Tab is trapped.
      // Capture + stopPropagation: when the drawer is the top dialog, its Escape must not
      // also close a dialog underneath (one press was closing drawer AND quickshop).
      document.addEventListener('keydown', function (e) {
        if (!self.isOpen) return;
        if (e.key === 'Escape') {
          e.stopPropagation();
          self.close();
        } else if (e.key === 'Tab') {
          self.trapFocus(e);
        }
      }, true);

      // Cart icon click - intercept
      document.addEventListener('click', function (e) {
        var toggle = e.target.closest(SELECTORS.toggle);
        if (toggle && !document.body.classList.contains('template-cart')) {
          e.preventDefault();
          self.fetchAndRender().then(function () {
            self.open();
          });
        }
      });

      // Intercept ATC banner checkout button and View Cart link
      // These should open the cart drawer, not go to checkout or cart page
      document.addEventListener('click', function (e) {
        if (document.body.classList.contains('template-cart')) return;

        // Intercept "Checkout" button in ATC banner
        var checkoutBtn = e.target.closest('.atc-button--checkout');
        if (checkoutBtn && checkoutBtn.closest('[data-atc-banner]')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          // Close the ATC banner
          document.dispatchEvent(new Event('closeFlyouts'));
          self.fetchAndRender().then(function () {
            self.open();
          });
          return;
        }

        // Intercept "View Cart" link in ATC banner
        var viewCartBtn = e.target.closest('.atc-button--viewcart');
        if (viewCartBtn && viewCartBtn.closest('[data-atc-banner]')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          document.dispatchEvent(new Event('closeFlyouts'));
          self.fetchAndRender().then(function () {
            self.open();
          });
          return;
        }
      }, true);

      // Also intercept the ATC banner checkout form submission
      document.addEventListener('submit', function (e) {
        if (document.body.classList.contains('template-cart')) return;
        var form = e.target;
        if (form.classList && form.classList.contains('atc-banner__form')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          document.dispatchEvent(new Event('closeFlyouts'));
          self.fetchAndRender().then(function () {
            self.open();
          });
        }
      }, true);

      // Fallback: listen for cartcount:update (e.g. from quickshop or other flows)
      window.addEventListener('cartcount:update', function () {
        if (document.body.classList.contains('template-cart')) return;
        // Immediately close Empire's ATC flyout banner so it doesn't flash
        document.dispatchEvent(new Event('closeFlyouts'));
        self.fetchAndRender().then(function () {
          self.open();
        });
      });
    },

    // Utility: format money using Shopify's built-in formatter
    formatMoney: function (cents) {
      var config = window.CartDrawerConfig || {};
      var format = config.moneyFormat || '${{amount}}';
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }
      // Fallback
      return '$' + (cents / 100).toFixed(2);
    },

    // Utility: get sized image URL
    getSizedImageUrl: function (url, size) {
      if (!url) return '';
      var match = url.match(/\.(jpg|jpeg|png|gif|webp|avif)(\?|$)/i);
      if (match) {
        return url.replace(match[0], '_' + size + '.' + match[1] + match[2]);
      }
      return url;
    },

    // Utility: escape HTML
    escapeHtml: function (str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      // textNode.innerHTML escapes &<> but NOT quotes — this output lands inside
      // double-quoted HTML attributes (img alt), so quotes must be escaped too.
      return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    },

    // Utility: get route
    getRoute: function (name) {
      if (window.Theme && window.Theme.routes) {
        if (name === 'cart') return window.Theme.routes.cart_url || '/cart';
      }
      return '/cart';
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { CartDrawer.init(); });
  } else {
    CartDrawer.init();
  }
})();
